import { useRef, useMemo, useCallback, useEffect } from 'react';
import { useWorkingHours } from '../hooks/api/workingHours';
import { postWorkingHour, postWorkingHours } from '../services/workingHours';
import { parseStringToFloat, parseLocalizedNumber } from '../utils/numberUtils';
import { useMutation } from '@tanstack/react-query';
import { useAlertMessage, useHEMTLocations, useWorkingUnits } from '../hooks';
import { weekDays } from '../utils/dateUtils';
import Container from '../components/Layout/Container';
import { Grid } from '@mui/material';
import ExportToExcelButton from '../components/Buttons/ExportToExcelButton';
import TableGridItem from '../components/UI/TableGridItem';
import WorkingUnitAutocomplete from '../components/Inputs/Autocomplete/WorkingUnitAutocomplete';
import {
    ColDef,
    ICellRendererParams,
    IsGroupOpenByDefaultParams,
    RowGroupOpenedEvent,
    ValueGetterParams,
    ValueSetterParams,
} from 'ag-grid-community';
import type { CellEditingStoppedEvent, CellValueChangedEvent } from 'ag-grid-community/dist/types/core/events';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { WorkingUnit } from '../types/WorkingUnit.interface';

const workingHoursDays = [...weekDays, { name: 'Feiertage', value: 8 }];
const exportColumNames = ['Standort', 'Worktype', 'Kanal', 'Task', 'Zeit', ...workingHoursDays.map((d) => d.name)];

const WorkingHours = () => {
    const gridRef = useRef(null);
    const { dispatchAlert } = useAlertMessage();
    const [expandedGroups, setExpandedGroups] = useLocalStorage<string[]>('working-hours-expanded-groups', []);

    const [workingUnitOptions] = useWorkingUnits({
        location: undefined,
    });
    const [selectedWorkingUnit, setSelectedWorkingUnit] = useLocalStorage<WorkingUnit | null>(
        'working-hours-selected-working-unit',
        null
    );

    const allLocationsFromHEMT = useHEMTLocations();
    const { data: tableRows, refetch: reloadWorkingHours } = useWorkingHours({
        workingUnit: selectedWorkingUnit?.id,
    });

    const columnDefs = useMemo<Array<ColDef>>(() => {
        const columns: Array<ColDef> = [
            {
                field: 'workingUnitLocation.locationId',
                headerName: 'Standort',
                valueGetter: (params: ValueGetterParams) => {
                    const location =
                        allLocationsFromHEMT &&
                        allLocationsFromHEMT.find((loc) => loc.id === params.data.workingUnitLocation.locationId);
                    return location ? `${location.code}-${location.city}` : '';
                },
                rowGroup: true,
                hide: true,
                editable: false,
            },
            {
                field: 'timeSlot',
                headerName: 'Time',
                editable: false,
            },
        ];

        for (const dayColumn of workingHoursDays) {
            columns.push({
                editable: true,
                hide: false,
                rowGroup: false,
                field: `day${dayColumn.value}`,
                headerName: dayColumn.name,
                cellRenderer: (params: ICellRendererParams) => {
                    let cellClass = 'text-center';

                    if (params?.data && params.data[`day${dayColumn.value}`]?.value === 1) {
                        cellClass += ' background-green';
                    }

                    return (
                        <span className={cellClass} style={{ textAlign: 'center' }}>
                            {params.value}
                        </span>
                    );
                },
                valueGetter: (params: ValueGetterParams) => {
                    if (
                        params.data &&
                        params.data[`day${dayColumn.value}`]?.value !== null &&
                        params.data[`day${dayColumn.value}`]?.value !== undefined
                    )
                        return params.data[`day${dayColumn.value}`]?.value;

                    return null;
                },
                valueSetter: (params: ValueSetterParams) => {
                    const parsed = parseLocalizedNumber(params.newValue);
                    // Reject invalid input — only accept valid numbers and empty (null)
                    if (parsed === null && params.newValue !== '' && params.newValue !== null) {
                        return false;
                    }
                    params.data[`day${dayColumn.value}`] = {
                        ...params.data[`day${dayColumn.value}`],
                        value: parsed,
                    };
                    return true;
                },
                suppressMovable: true,
                minWidth: 100,
            });
        }

        return columns;
    }, [allLocationsFromHEMT]);

    const { mutateAsync: handlePostWorkingHour } = useMutation({
        mutationFn: postWorkingHour,
    });
    const { mutateAsync: handlePostWorkingHours } = useMutation({
        mutationFn: postWorkingHours,
    });

    const saveWorkingHours = useCallback(
        async ({
            tableRows,
            workingHourData,
        }: {
            tableRows?: any;
            workingHourData?: {
                workingUnitLocationId: number;
                timeSlot: string;
                workingHourId: number;
                day: number;
                value: number;
            };
        }) => {
            try {
                if (workingHourData) {
                    await handlePostWorkingHour(workingHourData);
                } else {
                    await handlePostWorkingHours({
                        data: tableRows,
                    });
                }

                dispatchAlert({
                    message: 'Öffnungszeit gespeichert',
                    type: 'success',
                });
            } catch (error) {
                dispatchAlert({
                    message: 'Öffnungszeit konnte nicht gespeichert werden',
                    type: 'error',
                });
            } finally {
                reloadWorkingHours();
            }
        },
        [dispatchAlert, handlePostWorkingHour, handlePostWorkingHours, reloadWorkingHours]
    );

    const handleCellEdit = useCallback(
        (
            params: CellEditingStoppedEvent & {
                // @ts-ignore
                data: { workingUnitLocation: { id: number }; timeSlot: any; [x: string]: { workingHourId: any } };
            }
        ) => {
            const enteredValue = parseStringToFloat(params.value);
            if (enteredValue === 1 || enteredValue === 0) {
                const dayNumber = parseInt(params.column.getColId().substr(-1));
                saveWorkingHours({
                    workingHourData: {
                        workingUnitLocationId: params.data.workingUnitLocation.id,
                        timeSlot: params.data.timeSlot,
                        workingHourId: params.data[`day${dayNumber}`]?.workingHourId,
                        day: dayNumber,
                        value: enteredValue,
                    },
                });
            } else {
                dispatchAlert({
                    message: 'Geben Sie eine gültige Zahl ein',
                    type: 'error',
                });
            }
        },
        [dispatchAlert, saveWorkingHours]
    );

    const handleCellValueChange = useCallback(
        (
            params: CellValueChangedEvent & {
                data: { [x: string]: { changed: boolean } };
            }
        ) => {
            const dayNumber = parseInt(params.column.getColId().substr(-1));
            params.data[`day${dayNumber}`].changed = true;
        },
        []
    );

    const onPasteEnd = useCallback(() => {
        let numOfInvalidValues = 0;
        let numOfChangedCells = 0;
        for (const row of tableRows) {
            for (const cell of Object.keys(row)) {
                if (row[cell].changed === true) {
                    numOfChangedCells++;
                    const newValue = parseStringToFloat(row[cell].value);
                    if (newValue !== 1 && newValue !== 0) {
                        numOfInvalidValues++;
                    }
                }
            }
        }
        if (numOfInvalidValues === numOfChangedCells) {
            dispatchAlert({
                message: 'Geben Sie gültige Daten ein',
                type: 'error',
            });
        } else {
            saveWorkingHours({
                tableRows,
            });
        }
    }, [dispatchAlert, saveWorkingHours, tableRows]);

    const autoGroupColumnDef = useMemo(() => {
        return {
            minWidth: 230,
            headerName: '',
            resizable: true,
        };
    }, []);

    const createExcelRowForExportFile = useCallback(
        (exportDataRow: { [x: string]: { value: any }; workingUnitLocation?: any; timeSlot?: any }) => {
            const { workingUnitLocation, timeSlot } = exportDataRow;
            const { locationId, worktype, channel, task } = workingUnitLocation;

            const location = allLocationsFromHEMT.find((loc) => loc.id === locationId);
            const locationData = location ? `${location.code}-${location.city}` : '';

            const row = [locationData, worktype, channel, task, timeSlot];
            const days = Object.keys(exportDataRow).filter((key) => key.startsWith('day'));
            for (let i = 1; i <= 8; i++) {
                const day = days.find((day) => day.endsWith(String(i)));
                if (day) {
                    row.push(exportDataRow[day].value);
                } else {
                    row.push('');
                }
            }

            return row;
        },
        [allLocationsFromHEMT]
    );

    const isGroupOpen = useCallback(
        (params: IsGroupOpenByDefaultParams) => {
            return expandedGroups.includes(params.key);
        },
        [expandedGroups]
    );

    const handleGroupOpened = useCallback(
        (params: RowGroupOpenedEvent) => {
            if (params.node.expanded) {
                setExpandedGroups((prev) => [...prev, String(params.node.key)]);
            } else {
                setExpandedGroups((prev) => prev.filter((group) => group !== params.node.key));
            }
        },
        [setExpandedGroups]
    );

    useEffect(() => {
        if (workingUnitOptions.length > 0 && !selectedWorkingUnit) {
            setSelectedWorkingUnit(workingUnitOptions[0]);
        }
    }, [selectedWorkingUnit, setSelectedWorkingUnit, workingUnitOptions]);

    const handleSelectedWorkingUnitChange = useCallback(
        (workingUnit: null | WorkingUnit | WorkingUnit[]) => {
            setSelectedWorkingUnit(Array.isArray(workingUnit) ? workingUnit[0] : workingUnit);
            setExpandedGroups([]);
        },
        [setExpandedGroups, setSelectedWorkingUnit]
    );

    return (
        <Container wide={false}>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={10} md={6} lg={5}>
                    <WorkingUnitAutocomplete
                        selectedWorkingUnits={selectedWorkingUnit}
                        onChange={handleSelectedWorkingUnitChange}
                        workingUnitOptions={workingUnitOptions}
                        isMultiSelect={false}
                    ></WorkingUnitAutocomplete>
                </Grid>
                <Grid item xs={2} lg={1}>
                    <ExportToExcelButton
                        columnNames={exportColumNames}
                        sheetName="Tabelle1"
                        getFileName={() => 'Öffnungszeiten.xlsx'}
                        wbTitle="Öffnungszeiten"
                        wbSubject="Export Öffnungszeiten"
                        exportData={tableRows}
                        createRowFromExportData={createExcelRowForExportFile}
                        getColumnNames={undefined}
                        getExportData={undefined}
                        getDataFromDb={undefined}
                    />
                </Grid>
                <TableGridItem
                    ref={gridRef}
                    columnDefs={columnDefs}
                    animateRows={true}
                    rowData={tableRows}
                    autoGroupColumnDef={autoGroupColumnDef}
                    onPasteEnd={onPasteEnd}
                    onCellEditingStopped={handleCellEdit}
                    onCellValueChanged={handleCellValueChange}
                    suppressAggFuncInHeader={true}
                    isGroupOpenByDefault={isGroupOpen}
                    onRowGroupOpened={handleGroupOpened}
                    suppressScrollOnNewData={true}
                    cellSelection={true}
                ></TableGridItem>
            </Grid>
        </Container>
    );
};

export default WorkingHours;
